/**
 * MakingBiscuitsMinigame — Minna-only kneading rhythm session.
 *
 * Spec: `docs/specs/SPEC-making-biscuits.md`.
 *
 * Lifecycle:
 *   1. The scene spawns an `ElderBiscuitAnchor` per Elder. When Minna is
 *      within range and presses the activation key, the anchor calls
 *      `activate(elder, assistMode)` on this module.
 *   2. The module listens for alternating A/D taps, filling a trance
 *      depth meter from 0 → 100. A soft-fail (same paw twice or hit
 *      outside the window) only slows the fill — no resets, no game
 *      over.
 *   3. Reaching 100 emits `BISCUITS_COMPLETED`, which the owning scene
 *      (via `ElderBiscuitAnchor`) hands to `VisionRenderer.play(...)`.
 *   4. Walking away or pressing the activation key again cancels
 *      without consuming the vision.
 *
 * Accessibility:
 *   - `assistMode` fills the meter over `ASSIST_COMPLETE_SECONDS`
 *     regardless of input timing — the player only has to enter the
 *     mini-game and hold any paw key.
 *   - `beatWindowMultiplier` widens the per-pulse window.
 *
 * This class is a plain state machine — it does not subclass `Ability`
 * because the mini-game is not cooldown-gated and has a richer API than
 * the `activate()/deactivate()` pair the AbilitySystem expects. Scenes
 * hold a single shared instance per level.
 */
import { BISCUITS } from "../../config/GameConfig";
import { GameEvents } from "../../config/events";
import { gameEvents } from "../../core/EventBus";
import { AccessibilitySystem } from "../../systems/AccessibilitySystem";
import { BiscuitElder } from "../../systems/SaveSystem";

export type BiscuitPaw = "left" | "right";

export interface BiscuitSessionState {
  /** Which Elder's anchor the player is kneading. */
  elder: BiscuitElder;
  /** Trance depth [0..100]. When this reaches MAX_DEPTH, the vision fires. */
  depth: number;
  /** Last paw pressed — used to enforce alternation. */
  lastPaw: BiscuitPaw | null;
  /** Seconds since the last good beat. Drives the beat-window indicator. */
  secondsSinceLastBeat: number;
  /** Whether the player is in assist (auto-knead) mode. */
  assistMode: boolean;
  /** Total number of good hits in this session (for telemetry). */
  goodHits: number;
  /** Total number of soft-miss hits (same paw twice / out-of-window). */
  softMisses: number;
}

export class MakingBiscuitsMinigame {
  /** Null when no session is active. */
  private session: BiscuitSessionState | null = null;
  /** Anchor key — opaque string identifying which anchor drove activation. */
  private anchorKey: string | null = null;
  /** Anchor world position — used for HUD positioning. */
  private anchorPosition: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };

  /** True when a session is running (useful for input gating). */
  get isActive(): boolean {
    return this.session !== null;
  }

  /** Current session snapshot, or null when idle. */
  get state(): Readonly<BiscuitSessionState> | null {
    return this.session;
  }

  /** Which anchor is currently active. */
  get activeAnchorKey(): string | null {
    return this.anchorKey;
  }

  /**
   * Enter biscuit mode. Called by the anchor when Minna presses the
   * activation key. Returns true if the session was started, false if a
   * session is already running (the second press is swallowed by the
   * anchor as a cancel — see cancel()).
   */
  activate(
    elder: BiscuitElder,
    anchorKey: string,
    anchorPosition: { x: number; y: number; z: number },
    options?: { assistMode?: boolean },
  ): boolean {
    if (this.session !== null) return false;
    const a11y = AccessibilitySystem.getInstance().state;
    const assistMode = options?.assistMode ?? a11y.autoKneadAssist;

    this.anchorKey = anchorKey;
    this.anchorPosition = { ...anchorPosition };
    this.session = {
      elder,
      depth: 0,
      lastPaw: null,
      secondsSinceLastBeat: 0,
      assistMode,
      goodHits: 0,
      softMisses: 0,
    };

    gameEvents.emit(GameEvents.BISCUITS_STARTED, {
      anchorKey,
      elder,
      assistMode,
      position: this.anchorPosition,
    });
    return true;
  }

  /**
   * Process a paw press. The anchor routes the A/D keys through this.
   * Returns true if the press was accepted (used for input suppression).
   */
  pressPaw(paw: BiscuitPaw): boolean {
    if (!this.session) return false;

    // Assist mode: any paw press ticks the meter at the assist rate —
    // the per-frame update() does the actual filling.
    if (this.session.assistMode) {
      // Still emit a beat so the HUD can animate the paw pads.
      this.emitBeat(paw, "soft");
      return true;
    }

    // Normal rhythm: first tap is a "primer" that always counts (we have
    // no paw to alternate against). Subsequent taps must alternate AND
    // land within the pulse window to be "good"; same-paw-twice or
    // out-of-window register as soft misses.
    const isFirstTap = this.session.lastPaw === null;
    const alternating = !isFirstTap && this.session.lastPaw !== paw;
    const withinWindow = this.isWithinHitWindow();
    const good = isFirstTap || (alternating && withinWindow);

    if (good) {
      this.session.depth = Math.min(
        BISCUITS.MAX_DEPTH,
        this.session.depth + BISCUITS.BEAT_FILL,
      );
      this.session.goodHits += 1;
    } else {
      this.session.depth = Math.min(
        BISCUITS.MAX_DEPTH,
        this.session.depth + BISCUITS.SOFT_MISS_FILL,
      );
      this.session.softMisses += 1;
    }

    this.session.lastPaw = paw;
    this.session.secondsSinceLastBeat = 0;

    this.emitBeat(paw, good ? "good" : "soft");

    if (this.session.depth >= BISCUITS.MAX_DEPTH) {
      this.complete();
      return true;
    }

    return true;
  }

  /**
   * Advance the session timer. Must be called each frame by the owning
   * scene while a session is active. Handles:
   *   - Soft decay of the trance meter between inputs (when not in assist).
   *   - Assist-mode auto-fill over ASSIST_COMPLETE_SECONDS.
   *   - Firing `complete()` when depth reaches MAX_DEPTH.
   */
  update(dt: number): void {
    if (!this.session) return;
    this.session.secondsSinceLastBeat += dt;

    if (this.session.assistMode) {
      // Assist: linear fill toward MAX_DEPTH over ASSIST_COMPLETE_SECONDS.
      const fillPerSecond = BISCUITS.MAX_DEPTH / BISCUITS.ASSIST_COMPLETE_SECONDS;
      this.session.depth = Math.min(
        BISCUITS.MAX_DEPTH,
        this.session.depth + fillPerSecond * dt,
      );
    } else {
      // Normal: decay when the player isn't tapping. Depth never drops
      // below zero and never climbs on its own.
      if (this.session.secondsSinceLastBeat > 0.35) {
        this.session.depth = Math.max(
          0,
          this.session.depth - BISCUITS.DECAY_PER_SECOND * dt,
        );
      }
    }

    if (this.session.depth >= BISCUITS.MAX_DEPTH) {
      this.complete();
    }
  }

  /**
   * Cancel the session without firing the vision. Called by the anchor
   * when Minna walks out of range, presses the activation key again, or
   * switches characters.
   */
  cancel(reason: string): void {
    if (!this.session) return;
    const s = this.session;
    this.session = null;
    const key = this.anchorKey ?? "";
    this.anchorKey = null;
    gameEvents.emit(GameEvents.BISCUITS_CANCELLED, {
      anchorKey: key,
      elder: s.elder,
      reason,
    });
  }

  /** Internal: the session reached full depth. */
  private complete(): void {
    if (!this.session) return;
    const s = this.session;
    this.session = null;
    const key = this.anchorKey ?? "";
    this.anchorKey = null;
    gameEvents.emit(GameEvents.BISCUITS_COMPLETED, {
      anchorKey: key,
      elder: s.elder,
      replay: false, // Replay-vs-first-time is decided at the anchor; the
                    // mini-game just reports completion. ElderBiscuitAnchor
                    // consults SaveSystem to pick the right vision variant.
    });
  }

  /**
   * Whether a just-pressed paw lands inside the current pulse window.
   *
   * The design spec treats the window as ±(HIT_WINDOW_FRACTION × base
   * window) centered on the pulse boundary. Practically: a well-timed
   * player taps roughly every `BASE_BEAT_WINDOW_SECONDS` seconds, and
   * the window must be forgiving enough that a ±40% timing drift still
   * registers. The generous 0.6 fraction (60% of the period) is what
   * the spec's "generous window" language refers to.
   */
  private isWithinHitWindow(): boolean {
    const multiplier = AccessibilitySystem.getInstance().state.beatWindowMultiplier;
    const window = BISCUITS.BASE_BEAT_WINDOW_SECONDS * multiplier;
    // First beat — always within window (player hasn't tapped yet).
    if (this.session!.lastPaw === null) return true;
    // Subsequent beats — accept anything up to `window` seconds after
    // the prior beat. Longer gaps count as "out of rhythm" (soft miss).
    return this.session!.secondsSinceLastBeat <= window * BISCUITS.HIT_WINDOW_FRACTION;
  }

  private emitBeat(paw: BiscuitPaw, quality: "good" | "soft"): void {
    if (!this.session || !this.anchorKey) return;
    gameEvents.emit(GameEvents.BISCUITS_BEAT, {
      anchorKey: this.anchorKey,
      paw,
      quality,
      depth: this.session.depth,
    });
  }

  /** Clear any in-flight session (test hook, also called by scene dispose). */
  dispose(): void {
    if (this.session) {
      this.cancel("dispose");
    }
  }
}
